import {
  runDeletePublishedFileWorkflow,
  runRenameFileRecordWorkflow,
} from "@/action-dialog";
import {
  fileRecordKeys,
  useCopyFileRecord,
  useDeleteFileRecord,
} from "@/api/fileRecords";
import { generateUniqueName } from "@/components/FileConflictResolver";
import { FolderDropTarget } from "@/dnd/FolderDropTarget";
import { SelectableItem } from "@/dnd/SelectableItem";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import type { FileRecord } from "@cohesive/api-client";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { WorkspaceFileCardItem } from "../components/WorkspaceFileCardItem";
import { WorkspaceFolderCardItem } from "../components/WorkspaceFolderCardItem";
import { cascadeDeleteFolder } from "../utils/folderDelete";
import { handleWorkspaceItemActivate } from "../workspaceItemActivate";
import { useWorkspaceSelectionOptional } from "./useWorkspaceSelection";

/** Split "FolderA/foo.pdf" → { parent: "FolderA/", leaf: "foo.pdf" }. Root
 *  items return parent="". Folder paths keep their trailing `/` on the leaf
 *  stripped so `generateUniqueName` can operate on a clean name. */
function splitParentAndLeaf(path: string): { parent: string; leaf: string } {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const lastSlash = cleaned.lastIndexOf("/");
  if (lastSlash === -1) return { parent: "", leaf: cleaned };
  return {
    parent: cleaned.slice(0, lastSlash + 1),
    leaf: cleaned.slice(lastSlash + 1),
  };
}

/** Leaf names of the direct children of `parent` in `records`. Used to
 *  seed the "taken" set for sibling uniqueness checks. */
function getSiblingLeafNames(
  records: FileRecord[],
  parent: string,
): Set<string> {
  const names = new Set<string>();
  for (const r of records) {
    if (!r.path.startsWith(parent)) continue;
    const remainder = r.path.slice(parent.length);
    if (!remainder) continue;
    const cleaned = remainder.endsWith("/")
      ? remainder.slice(0, -1)
      : remainder;
    if (cleaned.length > 0 && !cleaned.includes("/")) names.add(cleaned);
  }
  return names;
}

/** Same as `getSiblingLeafNames` but over a set of in-flight claimed paths
 *  — so a bulk duplicate in the same folder doesn't pick the same "(n)"
 *  suffix twice while the earlier mutation is still on the wire. */
function getClaimedLeafNames(
  claimed: Set<string>,
  parent: string,
): Set<string> {
  const names = new Set<string>();
  for (const path of claimed) {
    if (!path.startsWith(parent)) continue;
    const remainder = path.slice(parent.length);
    if (!remainder) continue;
    const cleaned = remainder.endsWith("/")
      ? remainder.slice(0, -1)
      : remainder;
    if (cleaned.length > 0 && !cleaned.includes("/")) names.add(cleaned);
  }
  return names;
}

interface UseFileRecordCardActionsParams {
  /** When set, delete/copy optimistically patch this workspace list. Omit for canvas / flow-session scoped lists. */
  workspaceId?: string;
  allRecords: FileRecord[];
  onFolderOpen: (record: FileRecord) => void;
  onFileOpen?: (record: FileRecord) => void;
}

function invalidateFileRecordListCaches(
  queryClient: QueryClient,
  record: FileRecord,
) {
  if (record.class === "workspace" && record.workspace_id) {
    queryClient.invalidateQueries({
      queryKey: fileRecordKeys.workspace(record.workspace_id),
    });
    queryClient.invalidateQueries({
      queryKey: fileRecordKeys.recentlyDeleted(
        "workspace",
        record.workspace_id,
      ),
    });
    return;
  }
  if (record.class === "canvas" && record.canvas_id) {
    queryClient.invalidateQueries({
      queryKey: fileRecordKeys.canvas(record.canvas_id),
    });
    queryClient.invalidateQueries({
      queryKey: fileRecordKeys.recentlyDeleted("canvas", record.canvas_id),
    });
    return;
  }
  if (record.class === "flow-session" && record.flow_session_id) {
    queryClient.invalidateQueries({
      queryKey: fileRecordKeys.flowSession(record.flow_session_id),
    });
  }
}

export function useFileRecordCardActions({
  workspaceId,
  allRecords,
  onFolderOpen,
  onFileOpen,
}: UseFileRecordCardActionsParams) {
  const selection = useWorkspaceSelectionOptional();
  const openFileRecord = useOpenFileRecord();
  // Paths claimed by in-flight duplicate requests. Cleared per-mutation
  // in `onSettled` so bulk duplicates at the same parent pick distinct
  // "(n)" suffixes even before the server round-trips land and
  // `allRecords` refreshes.
  const claimedPathsRef = useRef<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const deleteFileRecord = useDeleteFileRecord(workspaceId, {
    onError: () => toast.error("Failed to delete"),
  });

  const copyFileRecord = useCopyFileRecord(workspaceId, {
    onSuccess: () => toast.success("Duplicated"),
    onError: () => toast.error("Failed to duplicate"),
  });

  const childCountMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of allRecords) {
      if (r.path.endsWith("/")) continue;
      const folders = allRecords.filter(
        (f) =>
          f.path.endsWith("/") &&
          r.path.startsWith(f.path) &&
          r.path !== f.path,
      );
      for (const folder of folders) {
        const isDirectChild =
          r.path.slice(folder.path.length).split("/").filter(Boolean).length ===
          1;
        if (isDirectChild) {
          map.set(folder.path, (map.get(folder.path) ?? 0) + 1);
        }
      }
    }
    for (const r of allRecords) {
      if (!r.path.endsWith("/")) continue;
      const folders = allRecords.filter(
        (f) =>
          f.path.endsWith("/") &&
          r.path.startsWith(f.path) &&
          r.path !== f.path,
      );
      for (const folder of folders) {
        const remainder = r.path.slice(folder.path.length);
        const remainderSegments = remainder
          .replace(/\/$/, "")
          .split("/")
          .filter(Boolean);
        if (remainderSegments.length === 1) {
          map.set(folder.path, (map.get(folder.path) ?? 0) + 1);
        }
      }
    }
    return map;
  }, [allRecords]);

  const handleFileOpen = useCallback(
    (record: FileRecord) => {
      if (onFileOpen) {
        onFileOpen(record);
        return;
      }
      // Default: route into `FileRecordDetailPane` (right pane). Replaces
      // the pre-FE-5 `window.open(record.url)` fallback — same preview
      // surface users see from chat attachments now, with proper back/
      // expand/close controls and a refreshable presigned URL instead of
      // a raw tab with an expiring S3 link.
      openFileRecord(record.id);
    },
    [onFileOpen, openFileRecord],
  );

  const handleCopy = useCallback(
    (record: FileRecord) => {
      // The backend's copy endpoint 409s on an exact path collision, so
      // the source path itself isn't a valid destination. Mirror the
      // upload/move "Keep both" UX: find the next available
      // "<name> (n).<ext>" at the same parent folder.
      const { parent, leaf } = splitParentAndLeaf(record.path);
      const isFolder = (record.path ?? "").endsWith("/");
      const taken = new Set<string>([
        ...getSiblingLeafNames(allRecords, parent),
        ...getClaimedLeafNames(claimedPathsRef.current, parent),
      ]);
      const uniqueLeaf = generateUniqueName(leaf, isFolder, taken);
      const newPath = `${parent}${uniqueLeaf}${isFolder ? "/" : ""}`;
      claimedPathsRef.current.add(newPath);

      copyFileRecord.mutate(
        {
          fileRecordId: record.id,
          payload: {
            destination: {
              class: record.class,
              workspace_id: record.workspace_id,
              canvas_id: record.canvas_id,
              flow_session_id: record.flow_session_id,
              path: newPath,
            },
          },
        },
        {
          onSettled: () => {
            claimedPathsRef.current.delete(newPath);
          },
        },
      );
    },
    [copyFileRecord, allRecords],
  );

  const handleDelete = useCallback(
    (record: FileRecord) => {
      // Folders need an explicit cascade — the API doesn't recursively delete
      // children, so we walk the tree deepest-first before removing the
      // folder itself. Files take the cheap single-shot path, gated by a
      // published-file confirmation when applicable so users don't
      // accidentally break a live public link with a single trash-icon click.
      if (record.path.endsWith("/")) {
        void cascadeDeleteFolder(
          record,
          allRecords,
          queryClient,
          workspaceId,
        ).catch(() => {
          toast.error("Failed to delete folder");
        });
        return;
      }
      void runDeletePublishedFileWorkflow({
        record,
        perform: () =>
          new Promise<void>((resolve, reject) => {
            deleteFileRecord.mutate(record.id, {
              onSuccess: () => resolve(),
              onError: () => reject(new Error("delete failed")),
              onSettled: () => {
                if (!workspaceId) {
                  invalidateFileRecordListCaches(queryClient, record);
                }
              },
            });
          }),
      });
    },
    [deleteFileRecord, allRecords, queryClient, workspaceId],
  );

  const handleRename = useCallback(
    (record: FileRecord) => {
      window.setTimeout(
        () => void runRenameFileRecordWorkflow({ record, allRecords }),
        0,
      );
    },
    [allRecords],
  );

  const renderFolderCard = useCallback(
    (record: FileRecord, _index: number) => {
      const selId = `folder:${record.id}`;
      const isSelected = selection?.isSelected(selId) ?? false;

      const card = (
        <WorkspaceFolderCardItem
          key={record.id}
          record={record}
          childCount={childCountMap.get(record.path) ?? 0}
          isSelected={isSelected}
          onClick={
            selection
              ? (e) =>
                  handleWorkspaceItemActivate({
                    isSelected,
                    e,
                    onOpen: () => onFolderOpen(record),
                    onSelect: () => selection.cardClick(selId, e),
                  })
              : () => onFolderOpen(record)
          }
          onDoubleClick={selection ? () => onFolderOpen(record) : undefined}
          handlerDeps={{
            onOpen: onFolderOpen,
            onRename: handleRename,
            onDelete: handleDelete,
          }}
        />
      );

      const wrapped = selection ? (
        <SelectableItem
          key={record.id}
          id={selId}
          kind="folder"
          selectedIds={selection.selectedIds}
          selectOnly={selection.selectOnly}
        >
          {card}
        </SelectableItem>
      ) : (
        card
      );

      if (!workspaceId) {
        return wrapped;
      }

      return (
        <FolderDropTarget
          key={record.id}
          folderPath={record.path}
          workspaceId={workspaceId}
        >
          {wrapped}
        </FolderDropTarget>
      );
    },
    [
      selection,
      childCountMap,
      workspaceId,
      onFolderOpen,
      handleRename,
      handleDelete,
    ],
  );

  const renderFileCard = useCallback(
    (record: FileRecord, _index: number) => {
      const selId = `file:${record.id}`;
      const isSelected = selection?.isSelected(selId) ?? false;

      const card = (
        <WorkspaceFileCardItem
          key={record.id}
          record={record}
          isSelected={isSelected}
          onClick={
            selection
              ? (e) =>
                  handleWorkspaceItemActivate({
                    isSelected,
                    e,
                    onOpen: () => handleFileOpen(record),
                    onSelect: () => selection.cardClick(selId, e),
                  })
              : () => handleFileOpen(record)
          }
          onDoubleClick={selection ? () => handleFileOpen(record) : undefined}
          handlerDeps={{
            onOpen: handleFileOpen,
            onRename: handleRename,
            onCopy: handleCopy,
            onDelete: handleDelete,
          }}
        />
      );

      if (!selection) return card;

      return (
        <SelectableItem
          key={record.id}
          id={selId}
          kind="file"
          selectedIds={selection.selectedIds}
          selectOnly={selection.selectOnly}
        >
          {card}
        </SelectableItem>
      );
    },
    [selection, handleFileOpen, handleRename, handleCopy, handleDelete],
  );

  const dialogs = null;

  const actions = {
    openFolder: onFolderOpen,
    openFile: handleFileOpen,
    rename: handleRename,
    copy: handleCopy,
    delete: handleDelete,
    getChildCount: (path: string) => childCountMap.get(path) ?? 0,
  };

  return { renderFolderCard, renderFileCard, dialogs, actions };
}
